import { useEffect, useState } from "react";
import { ExternalLink, Youtube, Podcast, AudioLines, Video, Mic } from "lucide-react";
import { api, ytId } from "@/components/Common";
import { Page, PageHeader, Loading } from "@/components/Common";

const TYPES = [
  { key: "", label: "سب" },
  { key: "youtube", label: "YouTube", icon: Youtube },
  { key: "podcast", label: "پوڈکاسٹ", icon: Podcast },
  { key: "audio", label: "آڈیو", icon: AudioLines },
  { key: "video", label: "ویڈیو", icon: Video },
  { key: "interview", label: "انٹرویو", icon: Mic },
];

const typeIcon = (t) => TYPES.find((x) => x.key === t)?.icon || Video;

const MediaCard = ({ item }) => {
  const id = ytId(item.url || "");
  const Icon = typeIcon(item.type);
  if (item.type === "youtube" && id) {
    return (
      <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(42,21,8,0.06)]" data-testid={`media-card-${item.slug}`}>
        <div className="aspect-video">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}`}
            title={item.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-5">
          <h3 className="text-xl font-semibold text-[#2a1508] leading-[2]">{item.title}</h3>
          {item.description && <p className="mt-1 text-base text-[#5c3a21] leading-[2.2]">{item.description}</p>}
        </div>
      </div>
    );
  }
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`media-card-${item.slug}`}
      className="group block bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(42,21,8,0.12)] hover:border-[#d4af37]"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#f5efe3] border border-[#d4af37]/50 flex items-center justify-center text-[#c5a059] group-hover:text-[#2a1508] transition-colors shrink-0">
          <Icon size={20} />
        </div>
        <h3 className="text-xl font-semibold text-[#2a1508] leading-[2]">{item.title}</h3>
      </div>
      {item.description && <p className="mt-3 text-base text-[#5c3a21] leading-[2.2]">{item.description}</p>}
      <span className="mt-3 inline-flex items-center gap-1 text-[#2a1508] group-hover:text-[#c5a059] transition-colors">
        کھولیں <ExternalLink size={14} />
      </span>
    </a>
  );
};

export default function MediaPage() {
  const [items, setItems] = useState(null);
  const [type, setType] = useState("");

  useEffect(() => {
    setItems(null);
    api.get("/media", { params: type ? { type } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [type]);

  return (
    <Page>
      <PageHeader title="اردو میڈیا" subtitle="YouTube، پوڈکاسٹس، آڈیو، ویڈیو اور انٹرویوز — اردو ڈیجیٹل میڈیا کا ذخیرہ" crumbs={[{ label: "اردو میڈیا" }]} />
      <div className="flex flex-wrap gap-2 mb-8" data-testid="media-type-tabs">
        {TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={`media-tab-${t.key || "all"}`}
            onClick={() => setType(t.key)}
            className={`px-5 py-1.5 rounded-full border text-base leading-[2] transition-colors ${
              type === t.key ? "bg-[#2a1508] text-[#f5efe3] border-[#2a1508]" : "border-[#c5a059]/60 text-[#5c3a21] hover:border-[#d4af37]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {!items ? (
        <Loading />
      ) : items.length === 0 ? (
        <p className="text-center text-lg text-[#5c3a21] py-16" data-testid="media-empty">اس زمرے میں مواد جلد شامل کیا جائے گا</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="media-grid">
          {items.map((m) => (
            <MediaCard key={m.slug} item={m} />
          ))}
        </div>
      )}
    </Page>
  );
}
